import {
  LabelSelector,
  type ScoreLabelOption,
  type LabelWithScore,
  type ScoreConfig,
  getScoreLabel,
  getScoreDescription,
} from '@core/components/LabelSelector';
import { ContentType, type StandardLabel } from '@system-config/label-management/domain';
import { useMemo } from 'react';

import type { DialogImageLabels } from '../../types';
import { useDialogContext } from '../DialogProvider';

import { stepFormStyles, inputGroupStyles } from './Steps.css';

/**
 * ImageLabelsStep component props
 */
export interface ImageLabelsStepProps {
  standardLabels: StandardLabel[];
  labelsLoading: boolean;
}

/**
 * ImageLabelsStep component - Step 3 of the dialog
 * Handles image content labeling configuration
 */
export function ImageLabelsStep({ standardLabels, labelsLoading }: ImageLabelsStepProps) {
  const { state, actions } = useDialogContext();
  const { imageLabels } = state.formData;

  // Filter standard labels for image content
  const imageLabelOptions: ScoreLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.IMAGE)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
        })),
    [standardLabels]
  );

  // Configuration for score threshold-based labels
  const scoreConfig: ScoreConfig = useMemo(
    () => ({
      mode: 'score' as const,
      min: 0,
      max: 100,
      defaultValue: 70,
      showDescription: true,
      getValueLabel: getScoreLabel,
      getValueDescription: getScoreDescription,
    }),
    []
  );

  const handleImageLabelsChange = (labels: LabelWithScore[]) => {
    const updatedImageLabels: DialogImageLabels = {
      imageLabels: labels,
    };

    actions.updateFormData({
      imageLabels: updatedImageLabels,
    });
  };

  return (
    <div className={stepFormStyles}>
      <div className={inputGroupStyles}>
        {imageLabelOptions.length > 0 ? (
          <LabelSelector
            enableBulkSelection
            availableLabels={imageLabelOptions}
            config={scoreConfig}
            emptyStateMessage="No image labels selected. Add labels to define image content criteria."
            loading={labelsLoading}
            selectedLabels={imageLabels.imageLabels}
            title="Image Content Labels"
            onChange={handleImageLabelsChange}
          />
        ) : (
          <div>{labelsLoading ? <p>Loading image labels...</p> : <p>No image labels available. Please configure labels in the system settings.</p>}</div>
        )}
      </div>
    </div>
  );
}
